import type React from "react";
import { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Container } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type translations from "../translations/translations.json";
import { createTruckGroupApi } from "../api/truckGroupsApi";
import { fetchTruckGroups } from "../store/features/truckGroupSlice";
import CreateTruckGroupForm from "../components/trucks/CreateTruckGroupForm";
import type { AppDispatch } from "../store";
import { fetchCities } from "../store/features/citySlice";
import {
  selectAllTrucks,
  selectAllTrucksLoading,
  fetchAllTrucks,
} from "../store/features/allTrucksSlice";
import type { RootState } from "../store";
import { getErrorMessage } from "../utils/errorHandling";

type TranslationKeys = keyof typeof translations.en;

const CreateTruckGroupPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { cities } = useSelector((state: RootState) => state.cities);
  const trucks = useSelector(selectAllTrucks);
  const trucksLoading = useSelector(selectAllTrucksLoading);
  const { user } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    dispatch(fetchCities());
    dispatch(fetchAllTrucks());
  }, [dispatch]);

  const handleSubmit = async (groupData: {
    name: string;
    truckIds: string[];
    editorUserIds: string[];
  }) => {
    setLoading(true);
    setError(null);
    try {
      const newGroup = await createTruckGroupApi(groupData);
      await dispatch(fetchTruckGroups()).unwrap();
      navigate(`/trucks/groups/${newGroup._id}`);
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "createTruckGroup");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading || trucksLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t("Create New Truck Group" as TranslationKeys)}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t("Create a new group of trucks to manage them together" as TranslationKeys)}
        </Typography>
      </Box>
      <CreateTruckGroupForm cities={cities} trucks={trucks} onSubmit={handleSubmit} error={error} />
    </Container>
  );
};

export default CreateTruckGroupPage;
